"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight, Video } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { GettyExternalEmbed } from "@/components/site/getty-embed";
import { TwitterEmbed } from "@/components/site/twitter-embed";
import {
  galleryAspectRatioCss,
  galleryVideoRender,
  galleryEmbedInput,
  galleryEmbedRender,
  galleryYouTubeThumbnailUrl,
  gallerySourceFallbackLabel,
  safeGallerySourceUrl,
  safeGalleryAspectRatio,
  safeGalleryFocalPercent,
  safeGalleryLayout,
  safeGalleryObjectFit,
  type ArticleGalleryAspectRatio,
  type ArticleGalleryLayout,
  type ArticleGalleryObjectFit,
} from "@/lib/article-gallery-media";
import type { ArticleGalleryItem } from "@/lib/article-content-blocks";
import {
  ARTICLE_MEDIA_DEFAULT_WIDTH,
  articleEmbedInnerClassName,
  articleEmbedInnerLayoutStyle,
  articleEmbedOuterLayoutStyle,
} from "@/lib/article-media-positioning";
import { cn } from "@/lib/utils";
import { socialEmbedAspectRatio, socialEmbedIframeSrc, socialEmbedTitle } from "@/lib/social-embeds";
import { externalEmbedAspectRatioCss, externalEmbedProviderLabel, type ExternalEmbed } from "@/lib/external-embed";

function altText(item: ArticleGalleryItem) {
  return item.alt || item.caption || item.title || item.sourceName || "";
}

function galleryShellClassName(layout: ArticleGalleryLayout) {
  if (layout === "full") return "article-embed-shell--full";
  if (layout === "normal") return "article-embed-shell--center";
  return "article-embed-shell--center";
}

function galleryInnerFallbackWidth(layout: ArticleGalleryLayout) {
  if (layout === "full") return null;
  if (layout === "normal") return 760;
  return 920;
}

export function galleryCounterClassName() {
  return "truncate rounded-[3px] bg-zinc-950/20 px-1.5 py-1 text-[10px] font-semibold uppercase leading-none tracking-[0.16em] text-white opacity-80 shadow-[0_1px_8px_rgba(0,0,0,0.22)] drop-shadow-[0_1px_3px_rgba(0,0,0,0.68)] backdrop-blur-[1px]";
}

export function galleryNavigationClassName() {
  return "pointer-events-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/60 bg-[#fffaf0]/62 text-zinc-950/70 shadow-[0_5px_16px_rgba(39,35,30,0.14)] backdrop-blur-md transition duration-200 hover:bg-[#fffaf0]/78 hover:text-zinc-950 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-white/70 sm:h-9 sm:w-9";
}

export function galleryCaptionClassName() {
  return "pointer-events-none absolute left-3 top-3 z-10 max-w-[calc(100%-1.5rem)] overflow-hidden rounded-md bg-neutral-950/48 px-3 py-1.5 text-xs font-semibold leading-5 text-white shadow-lg backdrop-blur-sm drop-shadow-[0_1px_4px_rgba(0,0,0,0.36)] sm:left-5 sm:top-5 sm:max-w-[min(34rem,calc(100%-2.5rem))]";
}

function gallerySource(item: ArticleGalleryItem) {
  return item.sourceName || item.credit || "";
}

export function GalleryCaptionSource({ item }: { item: ArticleGalleryItem }) {
  const caption = item.type === "text" && !item.description?.trim() ? "" : item.caption?.trim() ?? "";
  const source = gallerySource(item).trim();
  const sourceUrl = safeGallerySourceUrl(item.sourceUrl);
  const sourceLabel = source || (sourceUrl ? gallerySourceFallbackLabel(sourceUrl) : "");
  const ownContentLabel = item.ownContent ? "Laszti365" : "";
  if (!caption && !sourceLabel && !ownContentLabel) return null;
  return (
    <figcaption className="bg-white px-3 py-3 text-sm leading-6 text-zinc-700 sm:px-4">
      {caption ? <span>{caption}</span> : null}
      {sourceLabel ? (
        <span className={cn("text-xs font-medium text-zinc-500", caption ? "ml-2" : "")}>
          Forrás:{" "}
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="font-semibold underline decoration-zinc-300 underline-offset-2 transition hover:text-[#A4243B] hover:decoration-[#A4243B]">
              {sourceLabel}
            </a>
          ) : sourceLabel}
        </span>
      ) : null}
      {ownContentLabel ? (
        <span className={cn("text-xs font-medium text-zinc-500", caption || sourceLabel ? "ml-2" : "")}>
          {ownContentLabel}
        </span>
      ) : null}
    </figcaption>
  );
}

function MediaLoadFallback({ label = "A média nem tölthető be." }: { label?: string }) {
  return (
    <div className="grid h-full w-full place-items-center bg-zinc-950 px-7 text-center text-white">
      <div className="grid justify-items-center gap-3">
        <Video className="h-9 w-9 text-[#D8973C]" />
        <p className="text-sm font-black uppercase tracking-[0.18em]">{label}</p>
      </div>
    </div>
  );
}

function imagePositionStyle(item: ArticleGalleryItem): CSSProperties {
  const focalX = safeGalleryFocalPercent(item.focalX);
  const focalY = safeGalleryFocalPercent(item.focalY);
  return { objectPosition: `${focalX}% ${focalY}%` };
}

function GalleryImage({ item, fadeClassName, imageSizes }: { item: ArticleGalleryItem; fadeClassName: string; imageSizes: string }) {
  const [failed, setFailed] = useState(false);
  if (failed || !item.url) return <MediaLoadFallback />;
  return (
    <span className="absolute inset-0 block bg-zinc-100">
      <Image key={item.id} src={item.url} alt={altText(item)} fill sizes={imageSizes} className={cn("object-contain", fadeClassName)} style={imagePositionStyle(item)} loading="lazy" onError={() => setFailed(true)} />
    </span>
  );
}

function GalleryDirectVideo({ src, fitClassName, fadeClassName }: { src: string; fitClassName: string; fadeClassName: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <MediaLoadFallback />;
  return <video src={src} controls playsInline preload="metadata" onError={() => setFailed(true)} className={cn("h-full w-full bg-zinc-950", fitClassName, fadeClassName)} />;
}

function GalleryTextSlide({ item, fadeClassName }: { item: ArticleGalleryItem; fadeClassName: string }) {
  const title = item.title?.trim() ?? "";
  const body = item.description?.trim() || item.caption?.trim() || "";
  return (
    <div className={cn("grid h-full w-full place-items-center bg-[#FCFBF8] px-6 py-8 text-zinc-950 sm:px-10", fadeClassName)}>
      <div className="max-w-2xl text-center">
        {title ? <p className="text-2xl font-black leading-tight tracking-normal text-[#273E47] sm:text-4xl">{title}</p> : null}
        {body ? <p className={cn("whitespace-pre-line text-base font-semibold leading-7 text-zinc-700 sm:text-lg sm:leading-8", title && "mt-4")}>{body}</p> : null}
        {!title && !body ? <p className="text-sm font-black uppercase tracking-[0.18em] text-zinc-500">Szöveges galéria elem</p> : null}
      </div>
    </div>
  );
}

function GalleryEmbedThumbnailCard({ label, isGetty }: { label: string; isGetty: boolean }) {
  return (
    <span className="grid h-full place-items-center bg-[linear-gradient(135deg,#FFFDF8_0%,#F3E7D2_58%,#E7EEF0_100%)] px-2 text-center text-zinc-700">
      <span className="grid justify-items-center gap-0.5">
        {isGetty ? (
          <>
            <span className="text-[13px] font-black uppercase leading-none tracking-[0.18em] text-[#273E47]">Getty</span>
            <span className="text-[10px] font-black uppercase leading-none tracking-[0.22em] text-[#A4243B]">Images</span>
          </>
        ) : (
          <span className="max-w-[6.5rem] truncate text-[11px] font-black uppercase tracking-wide text-[#273E47]">{label}</span>
        )}
      </span>
    </span>
  );
}

function GalleryExternalEmbed({ item, fadeClassName }: { item: ArticleGalleryItem; fadeClassName: string }) {
  const render = galleryEmbedRender(galleryEmbedInput(item));
  if (!render || render.kind === "unsupported") return <MediaLoadFallback label="Nem támogatott embed" />;
  const { embed } = render;
  if (embed.provider === "social" && embed.social) {
    const social = embed.social;
    const isTwitterEmbed = social.platform === "x" || social.platform === "twitter";
    return isTwitterEmbed ? (
      <div className={cn("h-full w-full overflow-auto bg-[#FCFBF8]", fadeClassName)}>
        <TwitterEmbed url={social.url} fallbackText={social.fallbackText} className="mx-auto max-w-xl px-3 py-4" />
      </div>
    ) : (
      <div className={cn("grid h-full w-full place-items-center bg-[#FCFBF8]", fadeClassName)}>
        <iframe
          src={socialEmbedIframeSrc(social.embedUrl, 920)}
          title={socialEmbedTitle(social)}
          className="block h-full w-full max-w-3xl"
          style={{ aspectRatio: socialEmbedAspectRatio(social) }}
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  if (embed.provider === "getty" && (embed.itemId || item.itemId)) {
    return (
      <GettyExternalEmbed
        embed={embed}
        anchorId={embed.anchorId ?? item.anchorId}
        sig={embed.sig ?? item.sig}
        itemId={(embed.itemId || item.itemId) as string}
        width={embed.width ?? item.embedWidth}
        height={embed.height ?? item.embedHeight}
        caption={typeof embed.caption === "boolean" ? embed.caption : item.showCaption}
        tld={embed.tld ?? item.tld}
        is360={typeof embed.is360 === "boolean" ? embed.is360 : item.is360}
        className={fadeClassName}
      />
    );
  }
  if (embed.provider === "getty") return <MediaLoadFallback label="Getty embed nem tölthető be" />;
  return (
    <iframe
      src={embed.embedUrl}
      title={item.title || externalEmbedProviderLabel(embed)}
      className={cn("h-full w-full bg-[#FCFBF8]", fadeClassName)}
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
}

function galleryExternalEmbedAspectRatio(item: ArticleGalleryItem, embed: ExternalEmbed) {
  if (
    embed.provider === "getty" &&
    typeof (embed.width ?? item.embedWidth) === "number" &&
    Number.isFinite(embed.width ?? item.embedWidth) &&
    (embed.width ?? item.embedWidth)! > 0 &&
    typeof (embed.height ?? item.embedHeight) === "number" &&
    Number.isFinite(embed.height ?? item.embedHeight) &&
    (embed.height ?? item.embedHeight)! > 0
  ) {
    return `${embed.width ?? item.embedWidth} / ${embed.height ?? item.embedHeight}`;
  }
  return externalEmbedAspectRatioCss(embed);
}

export function ArticleGallerySlideMedia({
  item,
  aspectRatio,
  objectFit,
  imageSizes = "(max-width: 768px) 100vw, 1040px",
  fadeClassName = "opacity-0 transition duration-500 ease-out [animation:galleryFade_520ms_ease-out_forwards]",
}: {
  item: ArticleGalleryItem;
  aspectRatio: ArticleGalleryAspectRatio | string;
  objectFit: ArticleGalleryObjectFit | string;
  imageSizes?: string;
  fadeClassName?: string;
}) {
  const safeAspectRatio = safeGalleryAspectRatio(aspectRatio);
  const safeObjectFit = safeGalleryObjectFit(objectFit);
  const fitClassName = safeObjectFit === "contain" ? "object-contain" : "object-cover";
  const videoRender = item.type === "video" && item.url ? galleryVideoRender(item.url) : null;
  const embedRender = item.type === "embed" ? galleryEmbedRender(galleryEmbedInput(item)) : null;

  let content: ReactNode;
  if (item.type === "image" && item.url) {
    content = <GalleryImage key={`${item.id}:${item.url}`} item={item} fadeClassName={fadeClassName} imageSizes={imageSizes} />;
  } else if (item.type === "video" && item.url && videoRender?.kind === "direct") {
    content = <GalleryDirectVideo key={item.id} src={videoRender.src} fitClassName={fitClassName} fadeClassName={fadeClassName} />;
  } else if (item.type === "video" && item.url && videoRender?.kind === "youtube") {
    content = (
      <iframe
        key={item.id}
        src={videoRender.src}
        title={item.title || item.caption || "Galéria videó"}
        className={cn("h-full w-full bg-zinc-950", fadeClassName)}
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  } else if (item.type === "video" && videoRender?.kind === "social") {
    const embed = videoRender.embed;
    const iframeSrc = socialEmbedIframeSrc(embed.embedUrl, 920);
    const isTwitterEmbed = embed.platform === "x" || embed.platform === "twitter";
    content = isTwitterEmbed ? (
      <div key={item.id} className={cn("h-full w-full overflow-auto bg-[#FCFBF8]", fadeClassName)}>
        <TwitterEmbed url={embed.url} fallbackText={embed.fallbackText} className="mx-auto max-w-xl px-3 py-4" />
      </div>
    ) : (
      <div key={item.id} className={cn("grid h-full w-full place-items-center bg-[#FCFBF8]", fadeClassName)}>
        <iframe
          src={iframeSrc}
          title={socialEmbedTitle(embed)}
          className="block h-full w-full max-w-3xl"
          style={{ aspectRatio: socialEmbedAspectRatio({ ...embed, embedUrl: iframeSrc }) }}
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  } else if (item.type === "video" && item.url) {
    content = (
      <div key={item.id} className={cn("h-full w-full", fadeClassName)}>
        <MediaLoadFallback label="A média nem tölthető be." />
      </div>
    );
  } else if (item.type === "text") {
    content = <GalleryTextSlide item={item} fadeClassName={fadeClassName} />;
  } else if (item.type === "embed" && embedRender?.kind === "embed") {
    content = <GalleryExternalEmbed item={item} fadeClassName={fadeClassName} />;
  } else {
    content = (
      <div key={item.id} className={cn("h-full w-full", fadeClassName)}>
        <MediaLoadFallback />
      </div>
    );
  }

  const frameStyle = { aspectRatio: item.type === "embed" && embedRender?.kind === "embed" ? galleryExternalEmbedAspectRatio(item, embedRender.embed) : galleryAspectRatioCss(safeAspectRatio) } as CSSProperties;

  return (
    <div className={cn("relative w-full overflow-hidden", item.type === "image" ? "bg-zinc-100" : "bg-[#111]")} style={frameStyle}>
      {content}
      {item.title?.trim() ? <span className={galleryCaptionClassName()}>{item.title.trim()}</span> : null}
    </div>
  );
}

export function ArticleGalleryCarousel({
  items,
  layout,
  aspectRatio,
  objectFit,
  width,
}: {
  items: ArticleGalleryItem[];
  layout?: ArticleGalleryLayout | string;
  aspectRatio?: ArticleGalleryAspectRatio | string;
  objectFit?: ArticleGalleryObjectFit | string;
  width?: number | null;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const thumbnailStripRef = useRef<HTMLDivElement | null>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const touchStartX = useRef<number | null>(null);
  const safeLayout = safeGalleryLayout(layout);
  const safeAspectRatio = safeGalleryAspectRatio(aspectRatio);
  const safeObjectFit = safeGalleryObjectFit(objectFit);
  const total = items.length;
  const boundedActiveIndex = Math.min(activeIndex, Math.max(0, total - 1));
	  const active = items[boundedActiveIndex] ?? items[0];
	  const label = useMemo(() => `Galéria, ${boundedActiveIndex + 1}. elem / ${total}`, [boundedActiveIndex, total]);
	  const activeDescription = active ? `${boundedActiveIndex + 1}. elem: ${active.title || active.caption || (active.type === "image" ? "kép" : active.type === "video" ? "videó" : "szöveg")}` : label;

  function go(delta: number) {
    setActiveIndex((current) => (current + delta + total) % total);
  }

  useEffect(() => {
    thumbnailRefs.current[boundedActiveIndex]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [boundedActiveIndex]);

  if (!active) return null;
  const frameWidth = safeLayout === "full" ? null : (typeof width === "number" && Number.isFinite(width) ? width : galleryInnerFallbackWidth(safeLayout));
  const outerStyle = articleEmbedOuterLayoutStyle({ layout: safeLayout === "full" ? "full" : "center", width: frameWidth ?? ARTICLE_MEDIA_DEFAULT_WIDTH }) as CSSProperties;
  const innerStyle = articleEmbedInnerLayoutStyle({ layout: safeLayout === "full" ? "full" : "center", width: frameWidth ?? ARTICLE_MEDIA_DEFAULT_WIDTH }) as CSSProperties;

  return (
    <figure
      className={cn("not-prose article-media article-embed article-embed-shell my-12 text-zinc-950", galleryShellClassName(safeLayout))}
      style={outerStyle}
      aria-roledescription="carousel"
      aria-label={label}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          go(-1);
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          go(1);
        }
      }}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const startX = touchStartX.current;
        touchStartX.current = null;
        const endX = event.changedTouches[0]?.clientX;
        if (startX === null || endX === undefined || Math.abs(startX - endX) < 44) return;
        go(startX > endX ? 1 : -1);
      }}
    >
      <div className={cn(articleEmbedInnerClassName("overflow-hidden bg-white shadow-[0_18px_50px_rgba(39,35,30,0.10)] ring-1 ring-zinc-950/10 sm:rounded-lg"))} style={innerStyle}>
        <div className="relative grid bg-zinc-950">
	          <ArticleGallerySlideMedia item={active} aspectRatio={safeAspectRatio} objectFit={safeObjectFit} />
	          <p className="sr-only" aria-live="polite">{activeDescription}</p>

          <div className="pointer-events-none absolute inset-3 sm:inset-5">
            <p className={cn(galleryCounterClassName(), "absolute bottom-0 left-0")}>{boundedActiveIndex + 1} / {total}</p>

            {total > 1 ? (
              <>
                <button type="button" aria-label="Előző galéria elem" onClick={() => go(-1)} className={cn(galleryNavigationClassName(), "absolute left-0 top-1/2 -translate-y-1/2")}>
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.55} aria-hidden="true" />
                </button>
                <button type="button" aria-label="Következő galéria elem" onClick={() => go(1)} className={cn(galleryNavigationClassName(), "absolute right-0 top-1/2 -translate-y-1/2")}>
                  <ChevronRight className="h-4 w-4" strokeWidth={1.55} aria-hidden="true" />
                </button>
              </>
            ) : null}
          </div>
        </div>
        <GalleryCaptionSource item={active} />

      <div className="bg-white px-2 py-3 sm:px-3">
        <div ref={thumbnailStripRef} className="flex flex-nowrap snap-x gap-2 overflow-x-auto scroll-smooth px-1 py-1.5 [scrollbar-width:thin] sm:gap-3 sm:px-1.5" aria-label="Galéria pozíció">
          {items.map((item, index) => {
            const itemVideoRender = item.type === "video" && item.url ? galleryVideoRender(item.url) : null;
            const itemEmbedRender = item.type === "embed" ? galleryEmbedRender(galleryEmbedInput(item)) : null;
            return (
            <button
              ref={(element) => {
                thumbnailRefs.current[index] = element;
              }}
              key={item.id}
              type="button"
              aria-label={`${index + 1}. elem: ${item.title || item.caption || (item.type === "image" ? "kép" : item.type === "video" ? "videó" : "szöveg")}`}
              aria-current={index === boundedActiveIndex ? "true" : undefined}
              aria-pressed={index === boundedActiveIndex}
              onClick={() => setActiveIndex(index)}
              className={`group relative aspect-video w-28 shrink-0 snap-start overflow-hidden rounded-md border-2 bg-white text-left transition duration-200 sm:w-36 ${index === boundedActiveIndex ? "border-[#A4243B]" : "border-transparent opacity-80 hover:border-zinc-950/20 hover:opacity-100"}`}
            >
              {item.type === "image" && item.url ? (
                <span className="absolute inset-0 block bg-zinc-100">
                  <Image src={item.url} alt={altText(item)} fill sizes="144px" className="object-contain" style={imagePositionStyle(item)} />
                </span>
              ) : itemVideoRender?.kind === "direct" ? (
                <video src={itemVideoRender.src} muted preload="metadata" className="h-full w-full bg-zinc-950 object-cover" />
              ) : itemVideoRender?.kind === "youtube" ? (
                <span className="block h-full w-full bg-cover bg-center" style={{ backgroundImage: `url(${galleryYouTubeThumbnailUrl(itemVideoRender.videoId)})` }} />
              ) : itemEmbedRender?.kind === "embed" ? (
                <GalleryEmbedThumbnailCard label={externalEmbedProviderLabel(itemEmbedRender.embed)} isGetty={itemEmbedRender.embed.provider === "getty"} />
              ) : (
                <span className="grid h-full place-items-center bg-[#EEE6DA] px-3 text-center text-[11px] font-black uppercase tracking-wide text-zinc-600">
                  {item.type === "video" ? <Video className="h-5 w-5 text-[#A4243B]" /> : item.title || item.caption || "Szöveg"}
                </span>
              )}
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] font-black text-white">{index + 1} / {total}</span>
            </button>
            );
          })}
        </div>
      </div>
      <style jsx global>{`
        @keyframes galleryFade {
          from {
            opacity: 0;
            transform: scale(1.015);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
      </div>
    </figure>
  );
}
